<template>
  <div class="page">
    <div class="kpi-head">
      <h2 class="page-title" style="margin:0;">{{ $t('menu.kpi') }}</h2>
      <div class="kpi-controls">
        <el-date-picker
          v-model="range"
          type="daterange"
          value-format="yyyy-MM-dd"
          range-separator="-"
          :start-placeholder="$t('kpi.from')"
          :end-placeholder="$t('kpi.to')"
        />
        <el-select v-model="timezone" class="timezone-select">
          <el-option :label="$t('kpi.timezoneShanghai')" value="Asia/Shanghai" />
          <el-option :label="$t('kpi.timezoneUtc')" value="UTC" />
        </el-select>
        <el-button type="primary" class="btn-glass" :loading="loading" @click="reloadAll">{{ $t('kpi.apply') }}</el-button>
      </div>
    </div>

    <div class="bento-grid" style="margin-top:14px;">
      <div class="bento glass-card">
        <div class="muted">{{ $t('kpi.created') }}</div>
        <div class="kpi">{{ kpi ? kpi.createdCount : '-' }}</div>
      </div>
      <div class="bento glass-card">
        <div class="muted">{{ $t('kpi.closed') }}</div>
        <div class="kpi">{{ kpi ? kpi.closedCount : '-' }}</div>
      </div>
      <div class="bento glass-card">
        <div class="muted">{{ $t('kpi.avgFirstResponse') }}</div>
        <div class="kpi">{{ formatMinutes(kpi && kpi.avgFirstResponseMinutes) }}</div>
      </div>
      <div class="bento glass-card">
        <div class="muted">{{ $t('kpi.avgClose') }}</div>
        <div class="kpi">{{ formatMinutes(kpi && kpi.avgCloseMinutes) }}</div>
      </div>
      <div class="bento glass-card">
        <div class="muted">{{ $t('kpi.returned') }}</div>
        <div class="kpi">{{ kpi ? kpi.returnCount : '-' }}</div>
      </div>
      <div class="bento glass-card">
        <div class="muted">{{ $t('kpi.disputeRate') }}</div>
        <div class="kpi">{{ kpi && kpi.disputeRate!=null ? (kpi.disputeRate*100).toFixed(1)+'%' : '-' }}</div>
      </div>

      <div class="bento bento--full glass-card trend-card">
        <div class="trend-head">
          <div>
            <div class="audit-title">{{ $t('kpi.trend') }}</div>
            <div class="muted audit-subtitle">{{ $t('kpi.granularity') }}: {{ granularityLabel }}</div>
          </div>
          <el-radio-group v-model="trendMode" size="mini" @change="renderChart">
            <el-radio-button label="volume">{{ $t('kpi.ticketTrend') }}</el-radio-button>
            <el-radio-button label="efficiency">{{ $t('kpi.efficiencyTrend') }}</el-radio-button>
          </el-radio-group>
        </div>
        <div ref="chart" class="trend-chart"></div>
      </div>
    </div>

    <div class="glass-card audit-card">
      <el-tabs v-model="activeTab">
        <el-tab-pane :label="$t('kpi.caseAudit')" name="audit">
          <div class="audit-head">
            <div>
              <div class="audit-title">{{ $t('kpi.caseAudit') }}</div>
              <div class="muted audit-subtitle">{{ $t('kpi.caseAuditHint') }}</div>
            </div>
            <el-button @click="loadCases" :loading="caseLoading">{{ $t('common.refresh') }}</el-button>
          </div>

          <div class="audit-filters">
            <el-input
              v-model="caseFilters.q"
              :placeholder="$t('kpi.caseSearchPlaceholder')"
              clearable
              class="audit-search"
              @keyup.enter.native="applyCaseFilters"
            />
            <el-select v-model="caseFilters.status" clearable :placeholder="$t('field.status')" class="audit-status">
              <el-option v-for="s in ticketStatuses" :key="s" :label="$t(`status.ticket.${s}`)" :value="s" />
            </el-select>
            <el-select v-model="caseFilters.dispute" clearable :placeholder="$t('kpi.disputeFilter')" class="audit-status">
              <el-option :label="$t('kpi.disputeNone')" value="NONE" />
              <el-option :label="$t('kpi.disputeHas')" value="HAS" />
            </el-select>
            <el-select v-model="caseFilters.disputeStatus" clearable :placeholder="$t('kpi.disputeStatus')" class="audit-status">
              <el-option v-for="s in disputeStatuses" :key="s" :label="$t(`status.dispute.${s}`)" :value="s" />
            </el-select>
            <el-select v-model="caseFilters.disputeResult" clearable :placeholder="$t('kpi.disputeResult')" class="audit-status">
              <el-option :label="$t('dispute.result.RESOLVED')" value="RESOLVED" />
              <el-option :label="$t('dispute.result.REJECTED')" value="REJECTED" />
            </el-select>
            <el-button type="primary" class="btn-glass" plain icon="el-icon-search" :loading="caseLoading" @click="applyCaseFilters">
              {{ $t('common.search') }}
            </el-button>
          </div>

          <el-table :data="caseItems" style="width:100%;" @row-click="openCase">
            <el-table-column prop="caseNo" :label="$t('field.caseNo')" width="140" />
            <el-table-column prop="title" :label="$t('field.title')" min-width="220" show-overflow-tooltip />
            <el-table-column :label="$t('field.status')" width="110">
              <template slot-scope="{ row }">
                <span class="tag-pill">{{ caseStatusLabel(row) }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$i18n.locale === 'zh' ? '\u6d41\u8f6c' : 'Flow'" width="210">
              <template slot-scope="{ row }">
                <span class="tag-pill">{{ flowLabel(row) }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('ticket.disputeLabel')" width="110">
              <template slot-scope="{ row }">
                <span :class="['tag-pill', row.disputeStatus && row.disputeStatus !== 'DISPUTE_CLOSED' ? 'tag-pill--warn' : '']">
                  {{ disputeAuditLabel(row) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="createdByName" :label="$t('kpi.requester')" width="120" show-overflow-tooltip />
            <el-table-column prop="assignedHandlerName" :label="$t('kpi.handler')" width="120" show-overflow-tooltip />
            <el-table-column prop="assignedSecondaryHandlerName" :label="$t('role.SECONDARY_HANDLER')" width="140" show-overflow-tooltip />
            <el-table-column prop="createdAtLabel" :label="$t('field.createdAt')" width="170" />
            <el-table-column prop="closedAtLabel" :label="$t('field.closedAt')" width="170" />
            <el-table-column :label="$t('common.actions')" width="86" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" @click.stop="openCase(row)">{{ $t('common.view') }}</el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="audit-pagination">
            <el-pagination
              background
              layout="total, sizes, prev, pager, next"
              :total="caseTotal"
              :page-size="casePageSize"
              :current-page="casePage"
              :page-sizes="[5,10,20,50]"
              @current-change="onCasePageChange"
              @size-change="onCaseSizeChange"
            />
          </div>
        </el-tab-pane>

        <el-tab-pane :label="$t('kpi.processingStats')" name="processing">
          <el-table :data="processingRows" style="width:100%;">
            <el-table-column prop="label" :label="$t('field.type')" />
            <el-table-column prop="value" :label="$t('kpi.count')" width="160" />
          </el-table>
        </el-tab-pane>

        <el-tab-pane :label="$t('kpi.disputeStats')" name="disputes">
          <el-table :data="disputeRows" style="width:100%;">
            <el-table-column prop="label" :label="$t('field.type')" />
            <el-table-column prop="value" :label="$t('kpi.count')" width="160" />
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </div>

    <HandlerCaseDialog
      v-model="caseDialog"
      :case-no="currentCaseNo"
      :read-only="true"
      :dispute-read-only="true"
      :detail-loader="loadCaseDetail"
    />
  </div>
</template>

<script>
import * as echarts from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([BarChart, LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);
import { getKpiDashboard, pageKpiCases, getKpiCaseDetail } from '../../api/kpi';
import HandlerCaseDialog from '../../components/HandlerCaseDialog.vue';
import { caseStatusLabel as formatCaseStatusLabel, flowLabel as formatFlowLabel } from '../../utils/caseDisplay';

export default {
  name: 'KpiDashboard',
  components: { HandlerCaseDialog },
  data() {
    return {
      loading: false,
      range: this.defaultRange(),
      timezone: 'Asia/Shanghai',
      trendMode: 'volume',
      activeTab: 'audit',
      kpi: null,
      chart: null,
      caseLoading: false,
      caseItems: [],
      caseTotal: 0,
      casePage: 1,
      casePageSize: 10,
      caseFilters: { q: '', status: '', dispute: '', disputeStatus: '', disputeResult: '' },
      ticketStatuses: ['PENDING', 'PENDING_SECONDARY', 'ESCALATED', 'ESCALATED_RETURNED', 'IN_PROGRESS', 'WAITING_RESUBMIT', 'CLOSED', 'CANCELED'],
      disputeStatuses: ['DISPUTE_SECONDARY_CREATED', 'DISPUTE_SECONDARY_IN_PROGRESS', 'DISPUTE_CREATED', 'DISPUTE_IN_PROGRESS', 'DISPUTE_CLOSED'],
      caseDialog: false,
      currentCaseNo: ''
    };
  },
  computed: {
    granularityLabel() {
      const g = this.kpi && this.kpi.trendGranularity ? this.kpi.trendGranularity : 'DAY';
      const key = `kpi.granularity${g.charAt(0)}${g.slice(1).toLowerCase()}`;
      return this.$i18n.te(key) ? this.$t(key) : g;
    },
    processingRows() {
      return this.metricRows(this.kpi && this.kpi.processingStats, {
        handlerTaken: this.$t('kpi.handlerTaken'),
        handlerClosed: this.$t('kpi.handlerClosed'),
        handlerReturned: this.$t('kpi.handlerReturned'),
        secondaryReceived: this.$t('kpi.secondaryReceived'),
        secondaryEscalated: this.$t('kpi.secondaryEscalated'),
        secondaryClosed: this.$t('kpi.secondaryClosed'),
        secondaryReturned: this.$t('kpi.secondaryReturned')
      });
    },
    disputeRows() {
      return this.metricRows(this.kpi && this.kpi.disputeStats, {
        disputeCreated: this.$t('kpi.disputeCreated'),
        disputeTaken: this.$t('kpi.disputeTaken'),
        disputeClosed: this.$t('kpi.disputeClosed'),
        disputeResolved: this.$t('kpi.disputeResolved'),
        disputeRejected: this.$t('kpi.disputeRejected'),
        avgDisputeMinutes: this.$t('kpi.avgDispute')
      });
    }
  },
  mounted() {
    this.chart = echarts.init(this.$refs.chart);
    this.reloadAll();
    window.addEventListener('resize', this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    if (this.chart) this.chart.dispose();
  },
  methods: {
    defaultRange() {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 6);
      return [this.formatDate(start), this.formatDate(end)];
    },
    formatDate(d) {
      const x = new Date(d);
      const y = x.getFullYear();
      const m = String(x.getMonth() + 1).padStart(2, '0');
      const dd = String(x.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    },
    rangeParams() {
      const params = { timezone: this.timezone };
      if (this.range && this.range.length === 2) {
        params.from = this.range[0];
        params.to = this.range[1];
      }
      return params;
    },
    metricRows(items, labels) {
      return (items || []).map((x) => ({
        key: x.key,
        label: labels[x.key] || x.key,
        value: x.minutes == null ? Number(x.count || 0) : this.formatMinutes(x.minutes)
      }));
    },
    formatMinutes(v) {
      return v == null ? '-' : Number(v).toFixed(1);
    },
    caseStatusLabel(row) {
      return formatCaseStatusLabel(this, row);
    },
    flowLabel(row) {
      return formatFlowLabel(this, row);
    },
    onResize() {
      if (this.chart) this.chart.resize();
    },
    async reloadAll() {
      await this.load();
      await this.loadCases();
    },
    async load() {
      this.loading = true;
      try {
        this.kpi = await getKpiDashboard(this.rangeParams());
        this.renderChart();
      } finally {
        this.loading = false;
      }
    },
    async loadCases() {
      this.caseLoading = true;
      try {
        const params = {
          ...this.rangeParams(),
          q: this.caseFilters.q || undefined,
          status: this.caseFilters.status || undefined,
          dispute: this.caseFilters.dispute || undefined,
          disputeStatus: this.caseFilters.disputeStatus || undefined,
          disputeResult: this.caseFilters.disputeResult || undefined,
          page: this.casePage - 1,
          size: this.casePageSize
        };
        const r = await pageKpiCases(params);
        this.caseItems = (r.items || []).map((x) => ({
          ...x,
          assignedHandlerName: x.assignedHandlerName || '-',
          assignedSecondaryHandlerName: x.assignedSecondaryHandlerName || '-',
          createdAtLabel: this.formatDateTime(x.createdAt),
          closedAtLabel: this.formatDateTime(x.closedAt)
        }));
        this.caseTotal = r.total || 0;
      } finally {
        this.caseLoading = false;
      }
    },
    formatDateTime(value) {
      if (!value) return '-';
      return new Date(value).toLocaleString(undefined, {
        timeZone: this.timezone === 'UTC' ? 'UTC' : 'Asia/Shanghai',
        hour12: false
      });
    },
    applyCaseFilters() {
      this.casePage = 1;
      this.loadCases();
    },
    onCasePageChange(p) {
      this.casePage = p;
      this.loadCases();
    },
    onCaseSizeChange(s) {
      this.casePageSize = s;
      this.casePage = 1;
      this.loadCases();
    },
    openCase(row) {
      if (!row || !row.caseNo) return;
      this.currentCaseNo = row.caseNo;
      this.caseDialog = true;
    },
    loadCaseDetail(caseNo) {
      return getKpiCaseDetail(caseNo);
    },
    disputeAuditLabel(row) {
      if (!row || !row.disputeStatus) return this.$t('kpi.disputeNoneShort');
      if (row.disputeStatus !== 'DISPUTE_CLOSED') return this.$t('kpi.disputeActiveShort');
      const r = row.disputeResult;
      if (r === 'OK' || r === 'RESOLVED') return this.$t('dispute.result.RESOLVED');
      if (r === 'REJECT' || r === 'REJECTED') return this.$t('dispute.result.REJECTED');
      return this.$t('status.dispute.DISPUTE_CLOSED');
    },
    renderChart() {
      if (!this.chart || !this.kpi) return;
      const trend = this.kpi.trend || [];
      const xs = trend.map((p) => p.bucket);
      const option = {
        tooltip: { trigger: 'axis' },
        legend: { top: 0 },
        grid: { left: 44, right: 24, top: 42, bottom: 42 },
        xAxis: { type: 'category', data: xs, axisLabel: { hideOverlap: true } },
        yAxis: { type: 'value' },
        series: []
      };
      if (this.trendMode === 'efficiency') {
        option.series = [
          { name: this.$t('kpi.avgFirstResponse'), type: 'line', data: trend.map((p) => p.avgFirstResponseMinutes), smooth: true },
          { name: this.$t('kpi.avgClose'), type: 'line', data: trend.map((p) => p.avgCloseMinutes), smooth: true },
          { name: this.$t('kpi.avgDispute'), type: 'line', data: trend.map((p) => p.avgDisputeMinutes), smooth: true }
        ];
      } else {
        option.series = [
          { name: this.$t('kpi.created'), type: 'bar', data: trend.map((p) => p.created) },
          { name: this.$t('kpi.closed'), type: 'bar', data: trend.map((p) => p.closed) },
          { name: this.$t('kpi.returned'), type: 'line', data: trend.map((p) => p.returned), smooth: true },
          { name: this.$t('ticket.disputeLabel'), type: 'line', data: trend.map((p) => p.disputesCreated), smooth: true }
        ];
      }
      this.chart.setOption(option, true);
    }
  }
};
</script>

<style scoped>
.kpi-head,
.trend-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.kpi-controls,
.audit-filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.timezone-select {
  width: 168px;
}
.kpi {
  margin-top: 6px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 22px;
}
.trend-card,
.audit-card {
  padding: 14px;
}
.trend-chart {
  height: 320px;
  width: 100%;
  margin-top: 10px;
}
.audit-card {
  margin-top: 14px;
}
.audit-head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: space-between;
}
.audit-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
}
.audit-subtitle {
  margin-top: 4px;
}
.audit-filters {
  margin: 12px 0;
}
.audit-search {
  width: 240px;
}
.audit-status {
  width: 150px;
}
.audit-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}
@media (max-width: 760px) {
  .kpi-controls,
  .kpi-controls .el-date-editor,
  .timezone-select,
  .audit-search,
  .audit-status {
    width: 100% !important;
  }
}
</style>
